import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { BUSINESS_NAME, SUPPORT_WHATSAPP } from '../../config/brand';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Check, ChevronRight, ArrowLeft, PawPrint, Clock, IndianRupee, Sparkles, AlertCircle, MessageCircle, SkipForward } from 'lucide-react';
import { toast } from 'sonner';

const steps = ['Select Pet', 'Choose Service', 'Add-ons', 'Pick Time', 'Confirm'];

export default function BookAppointment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  const [step, setStep] = useState(0);
  const [pets, setPets] = useState([]);
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [taxConfig, setTaxConfig] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const autoAdvanceTimer = useRef(null);

  const [form, setForm] = useState({
    pet_id: '', service_ids: [], addon_ids: [], staff_id: '',
    date: '', time_slot: '', notes: '', special_requests: '',
    payment_method: 'online',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s, st] = await Promise.all([
          api.get('/pets'),
          api.get('/services'),
          api.get('/bookings/staff-list'),
        ]);
        setPets(p.data);
        setServices(s.data.filter(x => !x.is_addon));
        setAddons(s.data.filter(x => x.is_addon));
        setStaffList(st.data);

        if (preselectedService) {
          const matchedSvc = s.data.find(svc => svc.name === preselectedService);
          if (matchedSvc) {
            setForm(f => ({ ...f, service_ids: [matchedSvc.service_id] }));
          }
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const selectedPet = pets.find(p => p.pet_id === form.pet_id);

  const fetchDynamicPrices = useCallback(async (breed, species) => {
    if (!breed) { setDynamicPrices({}); return; }
    try {
      const allSvcs = [...services, ...addons];
      const priceMap = {};
      await Promise.all(allSvcs.map(async (svc) => {
        const res = await api.get(`/pricing/calculate?service_id=${svc.service_id}&breed=${encodeURIComponent(breed)}&species=${encodeURIComponent(species || '')}`);
        priceMap[svc.service_id] = res.data;
      }));
      setDynamicPrices(priceMap);
    } catch (e) { console.error('Price fetch error:', e); }
  }, [services, addons]);

  useEffect(() => {
    if (selectedPet && services.length > 0) {
      fetchDynamicPrices(selectedPet.breed, selectedPet.species);
    }
  }, [selectedPet, services.length, fetchDynamicPrices]);

  useEffect(() => {
    api.get('/pricing/tax').then(r => {
      const def = r.data?.find?.(t => t.is_default && t.active);
      if (def) setTaxConfig(def);
    }).catch(() => {});
  }, []);

  const getPrice = (svc) => {
    const dp = dynamicPrices[svc.service_id];
    return dp ? dp.final_price : svc.price;
  };
  const getDuration = (svc) => {
    const dp = dynamicPrices[svc.service_id];
    return dp ? dp.final_duration : svc.duration;
  };
  const hasOverride = (svc) => {
    const dp = dynamicPrices[svc.service_id];
    return dp?.has_override || false;
  };

  const availableServices = services.filter(svc => {
    if (!selectedPet || Object.keys(dynamicPrices).length === 0) return true;
    return dynamicPrices[svc.service_id]?.has_override === true;
  });
  const availableAddons = addons.filter(svc => {
    if (!selectedPet || Object.keys(dynamicPrices).length === 0) return true;
    return dynamicPrices[svc.service_id]?.has_override === true;
  });

  useEffect(() => {
    if (!form.date) return;
    const allSvcs = [...services, ...addons];
    const dur = [...form.service_ids, ...form.addon_ids].reduce((sum, sid) => {
      const svc = allSvcs.find(s => s.service_id === sid);
      return sum + (svc ? getDuration(svc) : 0);
    }, 0);
    api.get(`/bookings/available-slots?date=${form.date}&staff_id=${form.staff_id}&duration=${dur || 60}`)
      .then(r => setSlots(r.data)).catch(() => setSlots([]));
  }, [form.date, form.staff_id, form.service_ids, form.addon_ids, services, addons, dynamicPrices]);

  // Auto-advance: select pet → go to services
  const selectPet = (petId) => {
    const keepServices = preselectedService ? form.service_ids : [];
    setForm(f => ({ ...f, pet_id: petId, service_ids: keepServices, addon_ids: [], time_slot: '' }));
    setTimeout(() => setStep(1), 350);
  };

  const toggleService = (id) => {
    setForm(f => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter(x => x !== id) : [...f.service_ids, id],
    }));
  };

  const toggleAddon = (id) => {
    setForm(f => ({
      ...f,
      addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter(x => x !== id) : [...f.addon_ids, id],
    }));
  };

  // Auto-advance: select time slot → go to confirm
  const selectTimeSlot = (slot) => {
    setForm(f => ({ ...f, time_slot: slot }));
    setTimeout(() => setStep(4), 400);
  };

  const isCash = form.payment_method === 'cash';
  const allSvcs = [...services, ...addons];
  const subtotal = [...form.service_ids, ...form.addon_ids].reduce((sum, sid) => {
    const svc = allSvcs.find(s => s.service_id === sid);
    return sum + (svc ? getPrice(svc) : 0);
  }, 0);
  const gstRate = (!isCash && taxConfig?.rate) ? taxConfig.rate : 0;
  const gstAmount = Math.round(subtotal * gstRate) / 100;
  const totalPrice = Math.round((subtotal + gstAmount) * 100) / 100;
  const totalDuration = [...form.service_ids, ...form.addon_ids].reduce((sum, sid) => {
    const svc = allSvcs.find(s => s.service_id === sid);
    return sum + (svc ? getDuration(svc) : 0);
  }, 0);

  const canProceed = () => {
    if (step === 0) return !!form.pet_id;
    if (step === 1) return form.service_ids.length > 0;
    if (step === 2) return true;
    if (step === 3) return !!form.date && !!form.time_slot;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/bookings', form);
      setBookingResult(res.data);
      toast.success('Booking confirmed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappMessage = bookingResult
    ? `Hi ${BUSINESS_NAME}! I just booked an appointment for ${selectedPet?.name} (${selectedPet?.breed}) on ${form.date} at ${form.time_slot}. Booking ID: ${bookingResult.appointment_id}`
    : `Hi ${BUSINESS_NAME}! I'd like to enquire about grooming services.`;
  const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(whatsappMessage)}`;

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#6A3E28] border-t-transparent" /></div>;

  if (pets.length === 0) {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <PawPrint className="w-16 h-16 text-[#C48C68] mx-auto mb-4" strokeWidth={1.5} />
        <h2 className="text-xl font-bold text-[#3D2418] mb-2">No Pets Found</h2>
        <p className="text-sm text-[#7A6B5E] mb-6">Add a pet first before booking an appointment.</p>
        <Button className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220]" onClick={() => navigate('/client/pets/new')} data-testid="add-pet-btn">Add Pet</Button>
      </div>
    );
  }

  // Booking Success View
  if (bookingResult) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-6" data-testid="booking-success">
        <div className="w-20 h-20 bg-[#4A7C59]/10 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-10 h-10 text-[#4A7C59]" />
        </div>
        <h2 className="text-2xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Booking Confirmed!</h2>
        <p className="text-[#7A6B5E]">Your appointment has been booked successfully.</p>
        <Card className="rounded-2xl border-[#E8DDD4] text-left">
          <CardContent className="p-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#A09387]">Pet</span><span className="font-medium text-[#3D2418]">{selectedPet?.name}</span></div>
            <div className="flex justify-between"><span className="text-[#A09387]">Date</span><span className="font-medium text-[#3D2418]">{form.date}</span></div>
            <div className="flex justify-between"><span className="text-[#A09387]">Time</span><span className="font-medium text-[#3D2418]">{form.time_slot}</span></div>
            <div className="flex justify-between"><span className="text-[#A09387]">Total</span><span className="font-bold text-[#A96B49] flex items-center"><IndianRupee className="w-3 h-3" />{totalPrice.toFixed(2)}</span></div>
            {isCash && <div className="flex justify-between"><span className="text-[#A09387]">Payment</span><span className="font-medium text-[#3D2418]">Cash (No GST)</span></div>}
          </CardContent>
        </Card>
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 font-semibold text-sm hover:bg-[#1DA851] transition-colors shadow-md"
            data-testid="whatsapp-link"
          >
            <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
          </a>
          <Button className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220]" onClick={() => navigate('/client/appointments')} data-testid="view-appointments-btn">
            View Appointments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="book-appointment-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => step > 0 ? setStep(step - 1) : navigate('/client/dashboard')} className="rounded-full" data-testid="booking-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Book Appointment</h1>
          <p className="text-sm text-[#7A6B5E]">Step {step + 1} of {steps.length}: {steps[step]}</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 flex-1">
            <div className={`step-dot ${i < step ? 'completed' : i === step ? 'active' : ''}`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            {i < steps.length - 1 && <div className={`step-line ${i < step ? 'completed' : ''}`} />}
          </div>
        ))}
      </div>

      {/* Step 0: Select Pet — auto-advances on click */}
      {step === 0 && (
        <div>
          {preselectedService && (
            <div className="flex items-center gap-2 bg-[#A96B49]/10 border border-[#A96B49]/20 rounded-xl px-4 py-2.5 mb-4" data-testid="preselected-service-notice">
              <Sparkles className="w-4 h-4 text-[#A96B49] shrink-0" />
              <p className="text-sm text-[#6A3E28]">Booking <strong>{preselectedService}</strong> — select your pet to continue</p>
            </div>
          )}
          <p className="text-sm text-[#7A6B5E] mb-4">Tap your pet to continue</p>
          <div className="grid sm:grid-cols-2 gap-4">
          {pets.map(pet => (
            <Card
              key={pet.pet_id}
              className={`rounded-2xl cursor-pointer border-2 transition-all ${form.pet_id === pet.pet_id ? 'border-[#6A3E28] bg-[#F5EDE6] scale-[0.98]' : 'border-[#E8DDD4] bg-white hover:border-[#C48C68] hover:shadow-md'}`}
              onClick={() => selectPet(pet.pet_id)}
              data-testid={`select-pet-${pet.pet_id}`}
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F5EDE6] rounded-xl flex items-center justify-center">
                  <PawPrint className="w-6 h-6 text-[#6A3E28]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#3D2418]">{pet.name}</h3>
                  <p className="text-sm text-[#7A6B5E]">{pet.breed} | {pet.species}</p>
                  {pet.weight && <p className="text-xs text-[#A09387]">{pet.weight} kg</p>}
                </div>
                {form.pet_id === pet.pet_id && <Check className="w-5 h-5 text-[#4A7C59] shrink-0" />}
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
      )}

      {/* Step 1: Choose Services — inline Continue button */}
      {step === 1 && (
        <div>
          {selectedPet && Object.keys(dynamicPrices).length > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-4" data-testid="breed-pricing-notice">
              <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">Prices adjusted for <strong>{selectedPet.breed}</strong> breed</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {availableServices.map(svc => {
              const price = getPrice(svc);
              const duration = getDuration(svc);
              const override = hasOverride(svc);
              return (
                <Card
                  key={svc.service_id}
                  className={`rounded-2xl cursor-pointer border-2 transition-all ${form.service_ids.includes(svc.service_id) ? 'border-[#6A3E28] bg-[#F5EDE6]' : 'border-[#E8DDD4] bg-white hover:border-[#C48C68] hover:shadow-md'}`}
                  onClick={() => toggleService(svc.service_id)}
                  data-testid={`select-service-${svc.service_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-[#3D2418]">{svc.name}</h3>
                      {form.service_ids.includes(svc.service_id) && <Check className="w-5 h-5 text-[#4A7C59]" />}
                    </div>
                    <p className="text-sm text-[#7A6B5E] mb-3 line-clamp-2">{svc.description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1">
                          <IndianRupee className="w-4 h-4 text-[#A96B49]" />
                          <span className="text-lg font-bold text-[#A96B49]">{price.toFixed(0)}</span>
                          {override && <span className="text-xs line-through text-[#aaa] ml-1">{svc.price}</span>}
                        </div>
                        <span className="text-[10px] text-[#A09387]">plus 18% GST</span>
                      </div>
                      <span className="text-xs text-[#A09387] flex items-center gap-1">
                        <Clock className="w-3 h-3" />{duration} min
                        {override && duration !== svc.duration && <span className="line-through ml-1">{svc.duration}</span>}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Inline prominent Continue button */}
          {form.service_ids.length > 0 && (
            <div className="mt-6 animate-in fade-in slide-in-from-bottom-4">
              <Button
                className="w-full rounded-2xl bg-[#6A3E28] text-white hover:bg-[#5A3220] shadow-lg h-14 text-base font-semibold"
                onClick={() => setStep(2)}
                data-testid="service-continue-btn"
              >
                Continue with {form.service_ids.length} service{form.service_ids.length > 1 ? 's' : ''} <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Add-ons — with Skip & Continue */}
      {step === 2 && (
        <div>
          <p className="text-sm text-[#7A6B5E] mb-4">Optional add-on services to enhance your pet&apos;s experience.</p>
          {availableAddons.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-[#A09387] mb-4">No add-ons available for this breed</p>
              <Button className="rounded-2xl bg-[#6A3E28] text-white hover:bg-[#5A3220] shadow-lg h-12 px-10 text-base font-semibold" onClick={() => setStep(3)} data-testid="skip-addons-btn">
                Continue to Pick Time <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                {availableAddons.map(svc => {
                  const price = getPrice(svc);
                  const override = hasOverride(svc);
                  return (
                    <Card
                      key={svc.service_id}
                      className={`rounded-2xl cursor-pointer border-2 transition-all ${form.addon_ids.includes(svc.service_id) ? 'border-[#6A3E28] bg-[#F5EDE6]' : 'border-[#E8DDD4] bg-white hover:border-[#C48C68] hover:shadow-md'}`}
                      onClick={() => toggleAddon(svc.service_id)}
                      data-testid={`select-addon-${svc.service_id}`}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-[#3D2418] text-sm">{svc.name}</h3>
                          <p className="text-xs text-[#A09387]">{getDuration(svc)} min</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <span className="font-semibold text-[#A96B49] flex items-center">
                              <IndianRupee className="w-3 h-3" />{price.toFixed(0)}
                              {override && <span className="text-xs line-through text-[#aaa] ml-1">{svc.price}</span>}
                            </span>
                            <span className="text-[10px] text-[#A09387] block">plus 18% GST</span>
                          </div>
                          {form.addon_ids.includes(svc.service_id) && <Check className="w-4 h-4 text-[#4A7C59]" />}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Inline Continue / Skip buttons */}
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-2xl border-[#C48C68] text-[#6A3E28] h-12 text-sm font-semibold"
                  onClick={() => setStep(3)}
                  data-testid="skip-addons-btn"
                >
                  <SkipForward className="w-4 h-4 mr-2" /> Skip Add-ons
                </Button>
                {form.addon_ids.length > 0 && (
                  <Button
                    className="flex-1 rounded-2xl bg-[#6A3E28] text-white hover:bg-[#5A3220] shadow-lg h-12 text-sm font-semibold"
                    onClick={() => setStep(3)}
                    data-testid="addon-continue-btn"
                  >
                    Continue <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: Pick Date/Time/Staff */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Select Date *</Label>
              <input
                type="date"
                value={form.date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setForm({ ...form, date: e.target.value, time_slot: '' })}
                className="mt-1 w-full rounded-lg border border-[#E8DDD4] bg-[#FBF7F3] px-3 py-2 text-sm"
                data-testid="booking-date-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Groomer (optional)</Label>
              <Select value={form.staff_id || 'any'} onValueChange={(v) => setForm({ ...form, staff_id: v === 'any' ? '' : v, time_slot: '' })}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="booking-staff-select"><SelectValue placeholder="Any available" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Available</SelectItem>
                  {staffList.map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.date && (
            <div>
              <Label className="text-sm font-medium mb-3 block">Available Time Slots <span className="text-xs text-[#A09387] font-normal">(tap to select & continue)</span></Label>
              {slots.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-[#A09387] bg-[#FBF7F3] rounded-xl p-4">
                  <AlertCircle className="w-4 h-4" />
                  <span>No slots available for this date. Try a different date or groomer.</span>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {slots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => selectTimeSlot(slot)}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${form.time_slot === slot ? 'bg-[#6A3E28] text-white border-[#6A3E28] scale-95' : 'bg-white text-[#3D2418] border-[#E8DDD4] hover:border-[#C48C68] hover:shadow-sm'}`}
                      data-testid={`time-slot-${slot}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Payment Method */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Payment Method</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setForm({ ...form, payment_method: 'online' })}
                className={`rounded-xl border-2 p-4 text-left transition-all ${form.payment_method === 'online' ? 'border-[#6A3E28] bg-[#F5EDE6]' : 'border-[#E8DDD4] bg-white hover:border-[#C48C68]'}`}
                data-testid="payment-online-btn"
              >
                <p className="text-sm font-semibold text-[#3D2418]">Online Payment</p>
                <p className="text-xs text-[#A09387] mt-1">GST applicable (18%)</p>
              </button>
              <button
                onClick={() => setForm({ ...form, payment_method: 'cash' })}
                className={`rounded-xl border-2 p-4 text-left transition-all ${form.payment_method === 'cash' ? 'border-[#6A3E28] bg-[#F5EDE6]' : 'border-[#E8DDD4] bg-white hover:border-[#C48C68]'}`}
                data-testid="payment-cash-btn"
              >
                <p className="text-sm font-semibold text-[#3D2418]">Cash</p>
                <p className="text-xs text-[#A09387] mt-1">No GST</p>
              </button>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Notes / Special Requests</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-lg" rows={3} placeholder="Any special instructions for your groomer..." data-testid="booking-notes-input" />
          </div>
        </div>
      )}

      {/* Step 4: Confirm with GST breakdown */}
      {step === 4 && (
        <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
          <CardContent className="p-6 space-y-4" data-testid="booking-summary">
            <h3 className="text-lg font-semibold text-[#3D2418]">Booking Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-[#A09387]">Pet</span><span className="font-medium text-[#3D2418]">{selectedPet?.name} ({selectedPet?.breed})</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#A09387]">Date</span><span className="font-medium text-[#3D2418]">{form.date}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#A09387]">Time</span><span className="font-medium text-[#3D2418]">{form.time_slot}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#A09387]">Duration</span><span className="font-medium text-[#3D2418]">{totalDuration} min</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#A09387]">Payment</span><span className="font-medium text-[#3D2418]">{isCash ? 'Cash (No GST)' : 'Online (GST incl.)'}</span></div>
              {form.staff_id && (
                <div className="flex justify-between text-sm"><span className="text-[#A09387]">Groomer</span><span className="font-medium text-[#3D2418]">{staffList.find(s => s.user_id === form.staff_id)?.name || 'Any'}</span></div>
              )}

              <div className="border-t border-[#E8DDD4] pt-3">
                <p className="text-xs text-[#A09387] font-semibold uppercase mb-2">Services</p>
                {form.service_ids.map(sid => {
                  const svc = services.find(s => s.service_id === sid);
                  if (!svc) return null;
                  const price = getPrice(svc);
                  const override = hasOverride(svc);
                  return (
                    <div key={sid} className="flex justify-between text-sm mb-1">
                      <span className="text-[#3D2418]">{svc.name} {override && <span className="text-xs text-amber-600">(breed adj.)</span>}</span>
                      <span className="font-medium flex items-center"><IndianRupee className="w-3 h-3" />{price.toFixed(2)}</span>
                    </div>
                  );
                })}
                {form.addon_ids.map(sid => {
                  const svc = addons.find(s => s.service_id === sid);
                  if (!svc) return null;
                  const price = getPrice(svc);
                  return (
                    <div key={sid} className="flex justify-between text-sm text-[#7A6B5E] mb-1">
                      <span>{svc.name} (add-on)</span>
                      <span className="flex items-center"><IndianRupee className="w-3 h-3" />{price.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#E8DDD4] pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#A09387]">Subtotal</span>
                  <span className="font-medium flex items-center"><IndianRupee className="w-3 h-3" />{subtotal.toFixed(2)}</span>
                </div>
                {gstRate > 0 && (
                  <div className="flex justify-between text-sm" data-testid="gst-breakdown">
                    <span className="text-[#A09387]">GST ({gstRate}%)</span>
                    <span className="font-medium flex items-center"><IndianRupee className="w-3 h-3" />{gstAmount.toFixed(2)}</span>
                  </div>
                )}
                {isCash && (
                  <div className="flex justify-between text-sm" data-testid="no-gst-notice">
                    <span className="text-[#A09387]">GST</span>
                    <span className="text-xs text-[#4A7C59] font-medium">Not applicable (Cash)</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-dashed border-[#E8DDD4]">
                  <span className="text-base font-bold text-[#3D2418]">Total</span>
                  <span className="text-xl font-bold text-[#A96B49] flex items-center" data-testid="booking-total-price">
                    <IndianRupee className="w-4 h-4" />{totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            {form.notes && <div className="bg-[#FBF7F3] rounded-xl p-3"><p className="text-xs text-[#A09387] mb-1">Notes:</p><p className="text-sm text-[#3D2418]">{form.notes}</p></div>}

            {/* WhatsApp enquiry link */}
            <div className="pt-2 border-t border-[#E8DDD4]">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#25D366] hover:text-[#1DA851] font-medium"
                data-testid="whatsapp-enquiry-link"
              >
                <MessageCircle className="w-4 h-4" /> Questions? Chat on WhatsApp
              </a>
            </div>

            {/* Prominent Confirm Button — inline, not in sticky bar */}
            <Button
              className="w-full rounded-2xl bg-[#4A7C59] text-white hover:bg-[#3d6849] shadow-lg h-14 text-base font-semibold mt-4"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="booking-confirm-btn"
            >
              {submitting ? 'Confirming...' : 'Confirm Booking'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Spacer */}
      <div className="h-24" />

      {/* Sticky Bottom Bar — only shows on steps 0, 1, 3 as fallback */}
      {step !== 4 && step !== 2 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t-2 border-[#C48C68] px-4 py-3 z-[60] mb-[60px] md:mb-0" style={{ boxShadow: '0 -8px 24px rgba(106,62,40,0.12)' }} data-testid="booking-nav-bar">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <Button variant="outline" className="rounded-full border-[#C48C68] text-[#6A3E28] h-11" onClick={() => step > 0 ? setStep(step - 1) : navigate('/client/dashboard')} data-testid="booking-prev-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            {/* Price summary in sticky bar */}
            {subtotal > 0 && (
              <div className="hidden sm:flex items-center gap-1 text-sm text-[#3D2418]">
                <IndianRupee className="w-3.5 h-3.5" />
                <span className="font-bold">{subtotal.toFixed(0)}</span>
                <span className="text-xs text-[#A09387]">+ GST</span>
              </div>
            )}

            <Button
              className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220] shadow-lg h-11 px-8 text-sm font-semibold"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              data-testid="booking-next-btn"
            >
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
